/* eslint-disable @typescript-eslint/no-explicit-any */
import { isNumber } from 'lodash'
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import removeAccents from 'remove-accents'
import info from '../../assets/icons/info2.svg'
import ExamWithUnitAndResultInput from '../../components/examWithUnitAndResultInput'
import SearchBar from '../../components/searchBar'
import { useExams } from '../../hooks/exams'
import useDebounce from '../../utils/useDebounce'
import { InfoContainer, TypeExamsContainer } from './style'

export type TypeExamsValues = {
  examId: string;
  result?: string;
  unit?: string;
}

const useTypeExam = () => {
  const [values, setValues] = useState<TypeExamsValues[]>([])

  const events = useCallback((examId: string) => {
    return {
      onChange: (e: any) => {
        setValues(values => {
          const valueFromId = values.find((e) => e.examId === examId)
          const spreader = values.filter((e) => e.examId !== examId)
          return [
            ...spreader,
            { ...valueFromId, examId, result: e.target.value }
          ]
        })
      },
      onSelect: (e: any) => {
        setValues(values => {
          const valueFromId = values.find((e) => e.examId === examId)
          const spreader = values.filter((e) => e.examId !== examId)
          return [
            ...spreader,
            { ...valueFromId, examId, unit: e.value }
          ]
        })
      }
    }
  }, [setValues])

  const examValue = useCallback((examId) => {
    const valueFromId = values.find((e) => e.examId === examId)
    return {
      result: Number(valueFromId?.result || 0),
      unitSelected: valueFromId?.unit
    }
  }, [values])

  return { values, events, examValue }
}

type TypeExamsProps = {
  onChange?: (change: TypeExamsValues[]) => void;
}

const TypeExams: FC<TypeExamsProps> = ({ onChange }): ReactElement => {
  const { exams: _exams, get } = useExams()
  const { values, events, examValue } = useTypeExam()

  const [filter, setFilter] = useState('')

  useEffect(() => {
    get({ select: 'byRules' })
  }, [get])

  const debouncedValues = useDebounce(values, 200) as TypeExamsValues[]
  const debouncedFilter = useDebounce(filter, 200) as string

  useEffect(() => {
    if (onChange) {
      onChange(debouncedValues.filter(d => isNumber(d.result) && d.unit))
    }
  }, [debouncedValues])

  const exams = useMemo(() => {
    if (_exams && debouncedFilter) {
      const filter = removeAccents(debouncedFilter).toLowerCase()

      return _exams.filter(({ name }) => {
        const exists = removeAccents(name).toLowerCase().includes(filter)

        if (exists) {
          return exists
        }

        return false
      }).sort(({ name }) => {
        const exists = removeAccents(name).toLowerCase().includes(filter)

        if (exists) return -1

        return 1
      })
    }

    return _exams
  }, [_exams, debouncedFilter])

  return (
    <TypeExamsContainer>
      <InfoContainer>
        <img alt='info' src={info} />
        <div className='info_content'>
          Alguns exames não são mostrados aqui pois o sistema não possui
          regras de alerta relacionadas a eles, novas regras são adicionadas
          diariamente, e com isso a lista de exames para digitação também.
        </div>
      </InfoContainer>
      <SearchBar
        label='Procurar um exame'
        value={filter}
        onChange={(e: any) => setFilter(e.target.value)}
      />
      <div className='container_exams'>
        {exams.map(exam => (
          <ExamWithUnitAndResultInput
            key={exam._id}
            name={exam.name}
            unities={exam.measurements}
            {...events(exam._id)}
            {...examValue(exam._id)}
          />
        ))}
      </div>
    </TypeExamsContainer>
  )
}

export default TypeExams
